import React, { useMemo, useEffect, useRef } from "react";
import styles from "./GalleryAndVideo.module.css";
import { GalleryVideoService } from "../../services/gallery_video_service";
import config from "../../config/config";
import { useGalleryVideoState } from "../../states/useGalleryState";
import {
  FaPlay,
  FaImage,
  FaSearch,
  FaTimes,
  FaBolt,
  FaBullseye,
  FaCrown,
  FaYoutube,
  FaVimeo
} from "react-icons/fa";
import { GiCricketBat, GiCricket } from "react-icons/gi";
import picture from "./picture.png"
import video from "./youtube.png"
import { FaInfoCircle } from "react-icons/fa";
import gallery from "./gallery.png"

const GalleryAndVideo = () => {
  const galleryService = GalleryVideoService();
  const {
    activeTab,
    setActiveTab,
    selectedMedia,
    setSelectedMedia,
    searchTerm,
    setSearchTerm,
    filterMatch,
    setFilterMatch,
    galleryData,
    setGalleryData,
    loading,
    setLoading
  } = useGalleryVideoState();

  const videoRefs = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        await galleryService.fetchAllGalleryVideo(setGalleryData);
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalleryData();
  }, []);

  useEffect(() => {
    if (selectedMedia) {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.width = '100%';

      return () => {
        const scrollY = parseInt(document.body.style.top || '0') * -1;
        const scrollX = parseInt(document.body.style.left || '0') * -1;

        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';

        window.scrollTo(scrollX, scrollY);
      };
    }
  }, [selectedMedia]);

  const clearSearch = () => {
    setSearchTerm("");
  };

 const matchTypes = [
    { id: "t20", name: "T20", icon: <FaBolt /> },
    { id: "t10", name: "T10", icon: <FaBullseye /> },
    { id: "t40", name: "T40", icon: <FaCrown /> },
    { id: "all", name: "All Formats", icon: <GiCricketBat /> },
  ];

  // Function to get the full URL for media files
  const getFullMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;

    if (mediaPath.startsWith('http')) {
      return mediaPath;
    }

    if (mediaPath.startsWith('/media/')) {
      return `${config.baseURL}${mediaPath}`;
    }

    return `${config.baseURL}/media/${mediaPath}`;
  };

  // Function to check if it's YouTube video
  const isYouTubeVideo = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  // Function to check if it's Vimeo video
  const isVimeoVideo = (url) => {
    return url && url.includes('vimeo.com');
  };

  // Function to get video embed URL
  const getVideoEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;

    // YouTube
    if (isYouTubeVideo(videoUrl)) {
      let videoId = '';

      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1]?.split('&')[0];
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`;
      }
    }

    // Vimeo
    if (isVimeoVideo(videoUrl)) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
      }
    }

    return null;
  };

  // Function to get video preview (for YouTube videos)
  const getVideoPreview = (item) => {
    if (item.media_type === 'video' && item.media_url) {
      if (isYouTubeVideo(item.media_url)) {
        let videoId = '';

        if (item.media_url.includes('youtube.com/watch?v=')) {
          videoId = item.media_url.split('v=')[1]?.split('&')[0];
        } else if (item.media_url.includes('youtu.be/')) {
          videoId = item.media_url.split('youtu.be/')[1]?.split('?')[0];
        }

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=0&showinfo=0&autoplay=0&mute=1&loop=1&playlist=${videoId}`;
        }
      }
    }
    return null;
  };

  // Function to handle video loaded data
  const handleVideoLoaded = (itemId) => {
    const videoElement = videoRefs.current[itemId];
    if (videoElement) {
      // Seek to a specific time (e.g., 2 seconds) to show a frame
      videoElement.currentTime = 2;
    }
  };

  // Function to handle video error
  const handleVideoError = (itemId) => {

  };

  const filteredItems = useMemo(() => {
    return galleryData.filter((item) => {
      const matchesTab =
        activeTab === "all" ||
        (activeTab === "images" && item.media_type === "image") ||
        (activeTab === "videos" && item.media_type === "video");
      const matchesMatch =
        filterMatch === "all" || item.match_type === filterMatch;

      return matchesTab && matchesMatch;
    });
  }, [activeTab, filterMatch, galleryData]);

  const totalItems = galleryData.length;
  const imageCount = galleryData.filter((item) => item.media_type === "image").length;
  const videoCount = galleryData.filter((item) => item.media_type === "video").length;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Gallery...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <section className={styles.hero}>
          <div className={styles.heroBg}>
    <img src={gallery} alt="Gallery Banner" />
  </div>
    <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}><span>Royal Sports League</span></h1>
            <p className={styles.heroSubtitle}>
              Elite Moments & Highlights Gallery
            </p>
            <p className={styles.heroDescription} style={{ color: "white" }}>
              Relive the greatest cricket moments from the Royal Sports League.
              From thunderous sixes to magical bowling spells, experience the
              passion of cricket.
            </p>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.stat}>
              <span className={styles.statNumber}>{totalItems}</span>
              <span className={styles.statLabel}>Total Media</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>{imageCount}</span>
              <span className={styles.statLabel}>Photos</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statNumber}>{videoCount}</span>
              <span className={styles.statLabel}>Videos</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className={styles.filtersSection}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <h4>Media Type</h4>
            <div className={styles.tabFilters}>
              <button
                className={`${styles.tab} ${activeTab === "all" ? styles.active : ""}`}
                onClick={() => setActiveTab("all")}
              >
                All Content
              </button>
              <button
                className={`${styles.tab} ${activeTab === "images" ? styles.active : ""}`}
                onClick={() => setActiveTab("images")}
              >
                <FaImage /> Photos
              </button>
              <button
                className={`${styles.tab} ${activeTab === "videos" ? styles.active : ""}`}
                onClick={() => setActiveTab("videos")}
              >
                <FaPlay /> Videos
              </button>
            </div>
          </div>

          <div className={styles.filterGroup}>
            <h4>Match Format</h4>
            <div className={styles.matchFilters}>
              {matchTypes.map((match) => (
                <button
                  key={match.id}
                  className={`${styles.matchFilter} ${filterMatch === match.id ? styles.active : ""}`}
                  onClick={() => setFilterMatch(match.id)}
                >
                  <span className={styles.matchIcon}>{match.icon}</span>
                  {match.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cricket Moments Grid */}
      <section className={styles.mediaSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Cricket Moments</h2>
          <div className={styles.resultsInfo}>
            Showing {filteredItems.length} of {totalItems} moments
            {filterMatch !== "all" &&
              ` • ${matchTypes.find((m) => m.id === filterMatch)?.name}`}
          </div>
        </div>

        <div className={styles.mediaGrid}>
          {filteredItems.map((item) => {
            const videoPreviewUrl = getVideoPreview(item);
            const imageUrl = item.media_type === 'image'
              ? (item.media_url || getFullMediaUrl(item.media_file))
              : null;
            const videoUrl = item.media_type === 'video'
              ? (item.media_url || getFullMediaUrl(item.media_file))
              : null;

            return (
              <div
                key={item.id}
                className={styles.mediaCard}
                onClick={() => setSelectedMedia(item)}
              >
                {/* Media Display */}
                <div className={styles.mediaDisplay}>
                  {item.media_type === "image" ? (
                    imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="Cricket moment"
                        className={styles.mediaThumbnail}
                        loading="lazy"
                      />
                    ) : (
                      <div className={styles.mediaPlaceholder}>
                        <div className={styles.imagePlaceholder}>
                          {/* <FaImage />
                          <span>Image</span> */}
                          <img
                            src={picture}
                            className={styles.mediaThumbnail}
                          />
                        </div>
                      </div>
                    )
                  ) : (
                    // Video Display
                    <div className={styles.videoPreviewContainer}>
                      {/* YouTube Video */}
                      {videoPreviewUrl ? (
                        <iframe
                          src={videoPreviewUrl}
                          className={styles.videoPreview}
                          title="Video preview"
                          allow="autoplay; encrypted-media"
                          frameBorder="0"
                          scrolling="no"
                        />
                      ) : videoUrl ? (
                        // Local Video with thumbnail preview
                        <div className={styles.localVideoPreview}>
                          <video
                            ref={el => videoRefs.current[item.id] = el}
                            className={styles.videoThumbnail}
                            muted
                            preload="metadata"
                            onLoadedData={() => handleVideoLoaded(item.id)}
                            onError={() => handleVideoError(item.id)}
                          >
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                          <div className={styles.videoPlaceholderOverlay}>
                            <div className={styles.videoIconContainer}>
                              <FaPlay className={styles.videoIcon} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Fallback placeholder
                        <div className={styles.mediaPlaceholder}>
                          <div className={styles.videoPlaceholder}>
                            {/* <FaImage />
                          <span>Image</span> */}
                            <img
                              src={video}
                              className={styles.mediaThumbnail}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles.mediaBadges}>
                    <div className={styles.matchTypeBadge}>
                      {item.match_type !== "other" ? (
                        item.match_type?.toUpperCase()
                      ) : (item.other_match_type?.toUpperCase())}
                    </div>
                    {item.media_type === 'video' && item.media_url && (
                      <div className={styles.platformBadge}>
                        {isYouTubeVideo(item.media_url) ? (
                          <FaYoutube className={styles.youtubeIcon} />
                        ) : isVimeoVideo(item.media_url) ? (
                          <FaVimeo className={styles.vimeoIcon} />
                        ) : (
                          <FaPlay />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className={styles.noResults}>
            <span style={{fontSize: "2rem"}}><FaInfoCircle/></span>
            <h3>No cricket moments found</h3>
            <p>Try adjusting your filters</p>
          </div>
        )}
      </section>

      {selectedMedia && (
        <div className={styles.modalOverlay} onClick={() => setSelectedMedia(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={() => setSelectedMedia(null)}>
              <FaTimes />
            </button>

            <div className={styles.modalMedia}>
              {selectedMedia.media_type === "image" ? (
                <img
                  src={
                    selectedMedia.media_url
                      ? selectedMedia.media_url
                      : getFullMediaUrl(selectedMedia.media_file)
                  }
                  alt="Cricket moment"
                  className={styles.modalImage}
                />
              ) : (
                <div className={styles.videoContainer}>
                  {selectedMedia.media_url ? (
                    <iframe
                      src={getVideoEmbedUrl(selectedMedia.media_url)}
                      title="Cricket video"
                      className={styles.videoIframe}
                      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                      allowFullScreen
                      frameBorder="0"
                    />
                  ) : selectedMedia.media_file ? (
                    <video
                      controls
                      autoPlay
                      className={styles.localVideo}
                      poster={getFullMediaUrl(selectedMedia.media_file)}
                    >
                      <source src={getFullMediaUrl(selectedMedia.media_file)} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className={styles.videoPlaceholder}>
                      <p>No video source available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryAndVideo;